import { useCallback } from 'react';
import { CancelToken } from 'axios';
import useWorkOrders from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrders';
import useQuery, { QueryKey } from 'hooks/useQuery';
import { LinkedRecordsResponse } from 'screens/Applications&WorkOrders/WorkOrders/Hooks/linkedRecords.types';

/**
 * Kept in the shared query cache for as long as the route stays the same —
 * see useEvictLinkedRecordsCacheOnNavigation, which drops everything cached
 * under this prefix as soon as the route changes.
 */
export const LINKED_RECORDS_CACHE_KEY_PREFIX = 'linkedRecords';

/**
 * Every page fetched for a work order is cached until the route changes, so
 * paginating or switching between work orders never refetches something
 * already loaded.
 */
const useLinkedRecords = (
  workOrderId: string | null,
  page: number,
  size: number
) => {
  const { getLinkedRecords } = useWorkOrders();

  const queryFn = useCallback(
    (key: QueryKey, cancelToken?: CancelToken) => {
      if (!key || typeof key !== 'string') {
        throw new Error('Work order ID is required');
      }
      const [id, pageValue, sizeValue] = key.split('|');
      return getLinkedRecords(
        {
          workOrderId: id,
          page: Number(pageValue),
          size: Number(sizeValue)
        },
        cancelToken
      );
    },
    [getLinkedRecords]
  );

  const queryKey = workOrderId ? `${workOrderId}|${page}|${size}` : null;

  const { data, errors, isLoading, refreshCache, refetch } = useQuery<
    LinkedRecordsResponse
  >(queryKey, queryFn, {
    cacheKeyPrefix: LINKED_RECORDS_CACHE_KEY_PREFIX,
    // No TTL: the screen-level effect clears this namespace explicitly when
    // it's left, so a page/work order already loaded stays cached for as
    // long as the user is still on this screen.
    cacheTTL: Infinity
  });

  return {
    linkedRecords: data,
    errors,
    isLoading,
    refreshCache,
    refetch
  };
};

export default useLinkedRecords;
